import InternalTicketsRepositoryInterface, { SettingType, Ticket } from '@/services/Internal-Tickets/interfaces';
import PriorityEntity from '@/services/Internal-Tickets/interfaces/interfaces';
import ComponentEntity from '@/services/Internal-Tickets/interfaces/interfaces';
import { CreateTicketDto } from '@/services/Internal-Tickets/interfaces/create-ticket.dto';
import { AxiosInstance } from 'axios';
import { StatusEntity } from '@fenix/bug-report-entities/bug-report-entities';
import { UpdateSettingDto } from '@/services/Internal-Tickets/interfaces/setting.entity';
import UserData from '@/services/Internal-Tickets/interfaces/user.data';
import AssigneeEntity from '@/services/Internal-Tickets/interfaces/assignee.entity';
import CommentEntity from '@/services/Internal-Tickets/interfaces/comment.entity';
import NoteEntity from '@/services/Internal-Tickets/note.entity';


export default class InternalTicketsRepository implements InternalTicketsRepositoryInterface {

  constructor(
    private axios: AxiosInstance,
  ) {
  }

  public async getBugs(): Promise<Ticket[]> {
    const response = await this.axios.get('/internal-tickets/bugs');
    return response.data[0];
  }

  public async getFeatures(): Promise<Ticket[]> {
    const response = await this.axios.get('/internal-tickets/features');
    return response.data[0];
  }

  public async getComponents(): Promise<ComponentEntity[]> {
    const response = await this.axios.get('internal-tickets/settings/components');
    return response.data;
  }

  public async getPriorities(): Promise<PriorityEntity[]> {
    const response = await this.axios.get('internal-tickets/settings/priorities');
    return response.data;
  }

  public async getStatuses(): Promise<StatusEntity[]> {
    const response = await this.axios.get('internal-tickets/settings/statuses');
    return response.data;
  }

  public async createTicket(dto: CreateTicketDto): Promise<Ticket> {
    const response = await this.axios.post('/internal-tickets/tickets', dto);
    return response.data;
  }

  public async getArchive(): Promise<[Ticket[], number]> {
    const response = await this.axios.get('/internal-tickets/tickets', { params: { status: "Completed" }});
    return response.data;
  }

  public async getTicketInfo(id: number): Promise<Ticket> {
    const response = await this.axios.get(`/internal-tickets/tickets/${id}`);
    return response.data;
  }

  public async createSetting(type: SettingType, data: UpdateSettingDto): Promise<void> {
    if (type === SettingType.STATUS) {
      const response = await this.axios.post(`/internal-tickets/settings/statuses`, data);
    } else if (type === SettingType.PRIORITY) {
      const response = await this.axios.post(`/internal-tickets/settings/priorities`, data);
    } else if (type === SettingType.COMPONENT) {
      const response = await this.axios.post(`/internal-tickets/settings/components`, data);
    }
  }

  public async updateSetting(type: SettingType, id: number, dto: UpdateSettingDto): Promise<void> {
    if (type === SettingType.STATUS) {
      const response = await this.axios.patch(`/internal-tickets/settings/statuses/${id}`, dto);
    } else if (type === SettingType.PRIORITY) {
      const response = await this.axios.patch(`/internal-tickets/settings/priorities/${id}`, dto);
    } else if (type === SettingType.COMPONENT) {
      const response = await this.axios.patch(`/internal-tickets/settings/components/${id}`, dto);
    }
  }

  public async deleteSetting(type: SettingType, id: number): Promise<void> {
    if (type === SettingType.STATUS) {
      const response = await this.axios.delete(`/internal-tickets/settings/statuses/${id}`);
    } else if (type === SettingType.PRIORITY) {
      const response = await this.axios.delete(`/internal-tickets/settings/priorities/${id}`);
    } else if (type === SettingType.COMPONENT) {
      const response = await this.axios.delete(`/internal-tickets/settings/components/${id}`);
    }
  }

  public async createComment(ticketId: number, object: { message: string }): Promise<CommentEntity> {
    return await this.axios.post(`/internal-tickets/tickets/${ticketId}/comments`, object);
  }

  public async createNote(ticketId: number, object: { message: string }): Promise<NoteEntity> {
    return await this.axios.post(`/internal-tickets/tickets/${ticketId}/notes`, object);
  }

  public async getUsersData(dto: string[]): Promise<UserData[]> {
    const response = await this.axios.post('/employee-controller/profile/users-ids', dto);
    return response.data;
  }

  public async getAssignees(): Promise<AssigneeEntity []> {
    const response = await this.axios.get(`/internal-tickets/assignees`);
    return response.data;
  }

  public async refreshAssignees(): Promise<void> {
    await this.axios.post('/internal-tickets/assignees/refresh');
  }

  public async assignTicket(ticketId: string, assigneeId: string): Promise<void> {
    await this.axios.patch(`/internal-tickets/tickets/${ticketId}/assignee`, { assigneeId });
  }

  public async changeStatus(ticketId: string, statusId: number): Promise<void> {
    await this.axios.patch(`/internal-tickets/tickets/${ticketId}/status`, { statusId })
  }

  public async changeComponent(ticketId: string, componentId: number): Promise<void> {
    await this.axios.patch(`/internal-tickets/tickets/${ticketId}`, { componentId })
  }

  public async changePriority(ticketId: string, priorityId: number): Promise<void> {
    await this.axios.patch(`/internal-tickets/tickets/${ticketId}`, { priorityId })
  }

  public async getCommentsByTicketId(ticketId: string): Promise<CommentEntity[]> {
    const response = await this.axios.get(`/internal-tickets/tickets/${ticketId}/comments`)
    return response.data;
  }
}
